import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

interface GridLinesWrapperProps {
  children: React.ReactNode;
  className?: string;
  fadeBottom?: boolean;
  horizontalGap?: boolean;
  hideHorizontalLines?: boolean;
}

const GridLinesWrapper: React.FC<GridLinesWrapperProps> = ({ children, className, fadeBottom = false, horizontalGap = false, hideHorizontalLines = false }) => {
  const { theme } = useTheme();
  
  const lineColor = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  // Vertical line style with optional bottom fade
  const verticalLineStyle = fadeBottom 
    ? {
        background: `linear-gradient(to bottom, ${lineColor} 0%, ${lineColor} 60%, transparent 100%)`,
        opacity: 0,
        animation: 'gridLinesFadeIn 0.8s ease-out forwards'
      }
    : {
        backgroundColor: lineColor,
        opacity: 0,
        animation: 'gridLinesFadeIn 0.8s ease-out forwards'
      };

  const verticalLineStyleDelayed = fadeBottom
    ? {
        background: `linear-gradient(to bottom, ${lineColor} 0%, ${lineColor} 60%, transparent 100%)`,
        opacity: 0,
        animation: 'gridLinesFadeIn 0.8s ease-out 0.1s forwards'
      }
    : {
        backgroundColor: lineColor,
        opacity: 0,
        animation: 'gridLinesFadeIn 0.8s ease-out 0.1s forwards'
      };

  // Horizontal line gradient - with gap on right side (around phone) if horizontalGap is true
  const horizontalLineGradient = horizontalGap
    ? `linear-gradient(to right, transparent, ${lineColor} 5%, ${lineColor} 58%, transparent 62%, transparent 88%, ${lineColor} 92%, ${lineColor} 95%, transparent)`
    : `linear-gradient(to right, transparent, ${lineColor} 5%, ${lineColor} 95%, transparent)`;

  return (
    <div className={cn("relative grid-lines-container", className)}>
      {/* Keyframes for fade-in animation */}
      <style>{`
        @keyframes gridLinesFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      
      {/* Left vertical line */}
      <div 
        className="absolute top-0 bottom-0 left-[5%] md:left-[calc(50%-616px)] w-px pointer-events-none z-0 grid-line-left"
        style={verticalLineStyle}
      />
      
      {/* Right vertical line */}
      <div 
        className="absolute top-0 bottom-0 right-[5%] md:right-[calc(50%-616px)] w-px pointer-events-none z-0 grid-line-right"
        style={verticalLineStyleDelayed}
      />
      
      {/* Bottom horizontal lines (double) - top one faded, bottom one full width */}
      {!hideHorizontalLines && (
        <>
          {/* Top horizontal line - faded edges */}
          <div 
            className="absolute bottom-[38px] left-[5%] md:left-[calc(50%-616px)] right-[5%] md:right-[calc(50%-616px)] h-px pointer-events-none z-0"
            style={{ 
              background: horizontalLineGradient,
              opacity: 0,
              animation: 'gridLinesFadeIn 0.8s ease-out 0.2s forwards'
            }}
          />
          {/* Bottom horizontal line - full width (solid) */}
          <div 
            className="absolute bottom-0 left-[5%] md:left-[calc(50%-616px)] right-[5%] md:right-[calc(50%-616px)] h-px pointer-events-none z-0"
            style={{ 
              backgroundColor: lineColor,
              opacity: 0,
              animation: 'gridLinesFadeIn 0.8s ease-out 0.3s forwards'
            }}
          />
        </>
      )}
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default GridLinesWrapper;
